import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Master data › Flat chart — the source of truth. Saving carries the chart
// forward to every flat, booked sales, configurations, wing config and the
// project summary; the Booking board reads it directly.
const BOX = { background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '16px', border: '1px solid #e5e7eb' };
const H = { fontSize: '15px', fontWeight: 700, margin: '0 0 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' };
const IN = { width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const TH = { textAlign: 'left', fontSize: '12px', color: '#6b7280', fontWeight: 600, padding: '6px 8px', borderBottom: '1px solid #e5e7eb' };
const TD = { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontSize: '14px' };
const BTN = { padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px' };
const DEL = { ...BTN, borderColor: '#fca5a5', color: '#dc2626' };
const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const list = (a) => (a || []).join(', ');
const parseList = (t) => String(t || '').split(/[\s,]+/).map((x) => x.trim()).filter(Boolean).flatMap((x) => {
  const m = x.match(/^(\d+)-(\d+)$/); if (m) { const out = []; for (let i = +m[1]; i <= +m[2]; i++) out.push(i); return out; }
  return /^\d+$/.test(x) ? [Number(x)] : [];
});

const FlatChart = ({ apiBase, projectId }) => {
  const [c, setC] = useState(null);
  const [counts, setCounts] = useState([]);
  const [text, setText] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [labelFloor, setLabelFloor] = useState('');

  const load = () => {
    axios.get(`${apiBase}/booking-flow/chart`, { params: { project_id: projectId } })
      .then((r) => {
        setC(r.data.chart); setCounts(r.data.counts || []);
        setText({ refuge: list(r.data.chart.refuge_floors), podium: list(r.data.chart.podium_floors), amenity: list(r.data.chart.amenity_floors) });
      })
      .catch((e) => setErr(e.response?.data?.error || 'Could not load the flat chart'));
  };
  useEffect(load, [apiBase, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err && !c) return <div style={{ ...BOX, color: '#b91c1c' }}>{err}</div>;
  if (!c) return <div style={BOX}>Loading the flat chart…</div>;

  const typeNames = c.types.map((t) => t.type);
  const areaOf = (name) => c.types.find((t) => t.type === name)?.area || 0;
  const used = (name) => Object.values(c.series).includes(name) || Object.values(c.variants).includes(name);
  const upd = (patch) => { setC({ ...c, ...patch }); setResult(null); };
  const setType = (i, k, v) => {
    const types = c.types.map((t, j) => (j === i ? { ...t, [k]: v } : t));
    let series = c.series, variants = c.variants;
    if (k === 'type') { // rename follows through
      const old = c.types[i].type;
      series = Object.fromEntries(Object.entries(series).map(([s, n]) => [s, n === old ? v : n]));
      variants = Object.fromEntries(Object.entries(variants).map(([s, n]) => [s, n === old ? v : n]));
    }
    upd({ types, series, variants });
  };
  const flatsOfSeries = (s) => counts.filter((r) => String(r.unit_number) === String(s)).reduce((a, r) => a + r.n, 0);

  const save = async (dry) => {
    setBusy(true); setErr(''); setResult(null);
    const chart = { ...c, refuge_floors: parseList(text.refuge), podium_floors: parseList(text.podium), amenity_floors: parseList(text.amenity) };
    try {
      const r = await axios.put(`${apiBase}/booking-flow/chart`, { project_id: projectId, chart, dry_run: dry });
      setResult({ dry, ...r.data });
      if (!dry) load();
    } catch (e) {
      setErr(e.response?.status === 403 ? 'Only admins can change the flat chart.' : (e.response?.data?.error || 'Could not save'));
    } finally { setBusy(false); }
  };

  const series = Array.from({ length: Number(c.series_count) || 0 }, (_, i) => i + 1);
  const floorsRange = []; for (let f = Number(c.floors.max); f >= Number(c.floors.min); f--) floorsRange.push(f);

  return (
    <div>
      <div style={{ ...BOX, background: '#eff6ff', borderColor: '#bfdbfe', fontSize: '13px', color: '#1e3a8a' }}>
        <b>This chart is the source of truth.</b> Saving pushes it to every flat (type and carpet area), booked sales, the
        Configurations list, wing configuration and the project summary. The Booking board reads it directly. Use
        <b> Preview</b> first to see what would change.
      </div>

      <div style={BOX}>
        <div style={H}>
          <span>1 · Flat types</span>
          <button style={BTN} onClick={() => upd({ types: [...c.types, { type: `New type ${c.types.length + 1}`, bhk: '2BHK', area: 0 }] })}>+ Add flat type</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={TH}>Flat type</th><th style={TH}>BHK</th><th style={TH}>Carpet (sq ft)</th><th style={TH}>Used by</th><th style={TH} /></tr></thead>
          <tbody>
            {c.types.map((t, i) => (
              <tr key={i}>
                <td style={TD}><input style={IN} value={t.type} onChange={(e) => setType(i, 'type', e.target.value)} /></td>
                <td style={{ ...TD, width: '130px' }}>
                  <select style={IN} value={t.bhk} onChange={(e) => setType(i, 'bhk', e.target.value)}>
                    {['1BHK', '1.5BHK', '2BHK', '2.5BHK', '3BHK', '3.5BHK', '4BHK', '5BHK'].map((b) => <option key={b}>{b}</option>)}
                  </select>
                </td>
                <td style={{ ...TD, width: '140px' }}><input style={IN} type="number" step="0.01" value={t.area} onChange={(e) => setType(i, 'area', e.target.value)} /></td>
                <td style={{ ...TD, fontSize: '12px', color: '#6b7280' }}>
                  {Object.entries(c.series).filter(([, n]) => n === t.type).map(([s]) => `flat ${s}`).join(', ')}
                  {Object.entries(c.variants).filter(([, n]) => n === t.type).map(([b]) => ` · all ${b} flats`).join('')}
                </td>
                <td style={{ ...TD, width: '80px', textAlign: 'right' }}>
                  <button style={DEL} disabled={used(t.type)} title={used(t.type) ? 'In use — reassign its flats first' : 'Delete'}
                    onClick={() => upd({ types: c.types.filter((_, j) => j !== i) })}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={BOX}>
        <div style={H}>
          <span>2 · Flat series (flats per floor)</span>
          <span style={{ display: 'flex', gap: '6px' }}>
            <button style={BTN} onClick={() => upd({ series_count: Number(c.series_count) + 1 })}>+ Add flat no.</button>
            <button style={DEL} disabled={Number(c.series_count) <= 1} onClick={() => {
              const n = Number(c.series_count); const s = { ...c.series }; delete s[n];
              upd({ series_count: n - 1, series: s, jodi: c.jodi.filter((j) => !j.flats.includes(n)) });
            }}>Remove last</button>
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={TH}>Flat no.</th><th style={TH}>Flat type</th><th style={TH}>Carpet</th><th style={TH}>Flats in building</th></tr></thead>
          <tbody>
            {series.map((s) => (
              <tr key={s}>
                <td style={{ ...TD, width: '80px', fontWeight: 700 }}>{s}</td>
                <td style={TD}>
                  <select style={IN} value={c.series[s] || ''} onChange={(e) => upd({ series: { ...c.series, [s]: e.target.value } })}>
                    <option value="">—</option>
                    {typeNames.map((n) => <option key={n}>{n}</option>)}
                  </select>
                </td>
                <td style={{ ...TD, width: '110px' }}>{areaOf(c.series[s]) || '—'}</td>
                <td style={{ ...TD, width: '140px', color: '#6b7280' }}>{flatsOfSeries(s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: '14px', fontSize: '13px' }}>
          <b>Special flats by BHK</b> (overrides the series type, e.g. 3 BHK on refuge floors):
          {Object.entries(c.variants).map(([b, n]) => (
            <div key={b} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
              <span style={{ width: '90px' }}>All {b} flats →</span>
              <select style={{ ...IN, maxWidth: '260px' }} value={n} onChange={(e) => upd({ variants: { ...c.variants, [b]: e.target.value } })}>
                {typeNames.map((x) => <option key={x}>{x}</option>)}
              </select>
              <button style={DEL} onClick={() => { const v = { ...c.variants }; delete v[b]; upd({ variants: v }); }}>Remove</button>
            </div>
          ))}
          <button style={{ ...BTN, marginTop: '8px' }} onClick={() => {
            const b = window.prompt('BHK of the special flats (e.g. 3BHK)'); if (b && typeNames[0]) upd({ variants: { ...c.variants, [b.replace(/\s/g, '').toUpperCase()]: typeNames[0] } });
          }}>+ Add special flat rule</button>
        </div>
      </div>

      <div style={BOX}>
        <div style={H}><span>3 · Floor planning</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Lowest floor<input style={IN} type="number" value={c.floors.min} onChange={(e) => upd({ floors: { ...c.floors, min: e.target.value } })} /></label>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Top floor<input style={IN} type="number" value={c.floors.max} onChange={(e) => upd({ floors: { ...c.floors, max: e.target.value } })} /></label>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Rate (₹ / sq ft)<input style={IN} type="number" value={c.rate_per_sqft || ''} onChange={(e) => upd({ rate_per_sqft: e.target.value })} /></label>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>Refuge floors<input style={IN} value={text.refuge} placeholder="8, 15, 22" onChange={(e) => setText({ ...text, refuge: e.target.value })} /></label>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Podium parking floors<input style={IN} value={text.podium} placeholder="3-9" onChange={(e) => setText({ ...text, podium: e.target.value })} /></label>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#0d9488' }}>Amenities & fitness floors<input style={IN} value={text.amenity} placeholder="10" onChange={(e) => setText({ ...text, amenity: e.target.value })} /></label>
        </div>
        <div style={{ marginTop: '16px', fontSize: '13px' }}>
          <b>Named spaces</b> (non-flat spaces shown on the board, e.g. Fitness, Fire tank)
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px' }}>
            <tbody>
              {Object.entries(c.space_labels).sort((a, b) => b[0] - a[0]).map(([f, m]) => (
                <tr key={f}>
                  <td style={{ ...TD, width: '90px', fontWeight: 700 }}>{ordinal(Number(f))}</td>
                  <td style={TD}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {series.map((s) => (
                        <input key={s} title={`Flat ${s}`} placeholder={String(s)} value={m[s] || ''} style={{ ...IN, width: '84px' }}
                          onChange={(e) => upd({ space_labels: { ...c.space_labels, [f]: { ...m, [s]: e.target.value } } })} />
                      ))}
                    </div>
                  </td>
                  <td style={{ ...TD, width: '80px', textAlign: 'right' }}>
                    <button style={DEL} onClick={() => { const x = { ...c.space_labels }; delete x[f]; upd({ space_labels: x }); }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <select style={{ ...IN, maxWidth: '160px' }} value={labelFloor} onChange={(e) => setLabelFloor(e.target.value)}>
              <option value="">Pick floor…</option>
              {floorsRange.filter((f) => !c.space_labels[f]).map((f) => <option key={f} value={f}>{ordinal(f)}</option>)}
            </select>
            <button style={BTN} disabled={!labelFloor} onClick={() => { upd({ space_labels: { ...c.space_labels, [labelFloor]: {} } }); setLabelFloor(''); }}>+ Add floor spaces</button>
          </div>
        </div>
      </div>

      <div style={BOX}>
        <div style={H}>
          <span>4 · Jodi flats</span>
          <button style={BTN} onClick={() => upd({ jodi: [...c.jodi, { name: `Jodi ${c.jodi.length + 1}`, flats: [], area: null }] })}>+ Add jodi</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={TH}>Name</th><th style={TH}>Flats joined</th><th style={TH}>Combined area (sq ft)</th><th style={TH} /></tr></thead>
          <tbody>
            {c.jodi.map((j, i) => {
              const auto = Math.round(j.flats.reduce((a, f) => a + areaOf(c.series[f]), 0));
              const setJ = (patch) => upd({ jodi: c.jodi.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
              return (
                <tr key={i}>
                  <td style={{ ...TD, width: '160px' }}><input style={IN} value={j.name} onChange={(e) => setJ({ name: e.target.value })} /></td>
                  <td style={TD}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {series.map((s) => (
                        <label key={s} style={{ display: 'inline-flex', gap: '3px', alignItems: 'center', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '3px 6px', background: j.flats.includes(s) ? '#fdf2f8' : '#fff' }}>
                          <input type="checkbox" checked={j.flats.includes(s)} onChange={(e) => setJ({ flats: e.target.checked ? [...j.flats, s].sort((a, b) => a - b) : j.flats.filter((x) => x !== s) })} />{s}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td style={{ ...TD, width: '170px' }}>
                    <input style={IN} type="number" placeholder={`auto ${auto}`} value={j.area || ''} onChange={(e) => setJ({ area: e.target.value ? Number(e.target.value) : null })} />
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>sum of flats: {auto}</div>
                  </td>
                  <td style={{ ...TD, width: '80px', textAlign: 'right' }}><button style={DEL} onClick={() => upd({ jodi: c.jodi.filter((_, k) => k !== i) })}>Delete</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {err && <div style={{ ...BOX, background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' }}>{err}</div>}
      {result && (
        <div style={{ ...BOX, background: result.dry ? '#fffbeb' : '#ecfdf5', borderColor: result.dry ? '#fcd34d' : '#a7f3d0', fontSize: '14px' }}>
          <b>{result.message}</b>
          {result.changes && (
            <div style={{ marginTop: '6px' }}>
              Flats {result.dry ? 'that would change' : 'changed'}: <b>{result.changes.flats_changed}</b>
              {result.changes.unmatched ? ` · ${result.changes.unmatched} flats have no type in the chart` : ''}
              {result.changes.project && ` · project: ${result.changes.project.total} flats, ${result.changes.project.available} available, ${result.changes.project.booked} booked, carpet ${result.changes.project.carpet_range}`}
              <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                {Object.entries(result.changes.by_type || {}).sort().map(([t, n]) => `${t}: ${n}`).join(' · ')}
              </div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', position: 'sticky', bottom: 0, background: '#f8fafc', padding: '12px 0' }}>
        <button style={{ ...BTN, padding: '10px 18px' }} disabled={busy} onClick={() => save(true)}>Preview changes</button>
        <button className="btn btn-primary" style={{ padding: '10px 18px' }} disabled={busy}
          onClick={() => { if (window.confirm('Save the chart and update every flat, booked sales, configurations and the project summary?')) save(false); }}>
          {busy ? 'Working…' : 'Save & apply to all flats'}
        </button>
      </div>
    </div>
  );
};

export default FlatChart;
